// @ts-check
import { Component } from '@theme/component';
import { slideOpen } from '@theme/custom.slide-reveal';

/**
 * "Load more" control for the PDP media grid.
 *
 * Flips `data-gallery-expanded` on the host `media-gallery`; CSS does the revealing, and the lazy
 * media fetch as they appear. Not a disclosure: the pill hides itself once expanded, so
 * `aria-expanded` would describe a state the shopper never sees. A variant change replaces the
 * gallery element (assets/media-gallery.js), which re-collapses the grid by design.
 *
 * @extends {Component}
 */
class CustomGalleryMore extends Component {
  /**
   * Reveals the media the grid is holding back.
   *
   * @param {Event} event - The click event.
   */
  reveal = (event) => {
    event.preventDefault();

    const gallery = this.closest('media-gallery');
    if (!gallery) return;

    // Collected before the flip: the attribute is what un-hides the tiles, and a tile still at
    // `display: none` measures zero, so the reveal would animate from nothing to nothing.
    const extras = /** @type {HTMLElement[]} */ ([
      ...gallery.querySelectorAll('.custom-gallery-more__extra'),
    ]);

    gallery.setAttribute('data-gallery-expanded', '');

    // The same clip-open the review list's Load more uses, so the two controls on this page reveal
    // identically. It respects prefers-reduced-motion on its own.
    for (const tile of extras) slideOpen(tile);

    // This control is about to hide itself, which would drop focus to the document root.
    const tile = extras[0];
    if (!tile) return;

    // A tile only holds a button while the gallery's `zoom` setting is on, so the tile itself is
    // the fallback; -1 makes it focusable without adding a tab stop.
    const target = tile.querySelector('button, [href]');

    if (target instanceof HTMLElement) {
      target.focus();
      return;
    }

    tile.tabIndex = -1;
    tile.focus();
  };
}

if (!customElements.get('custom-gallery-more')) {
  customElements.define('custom-gallery-more', CustomGalleryMore);
}
